import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GameScreenShell from "@components/Home/Games/GameScreenShell";
import SessionSummary, { SessionSummaryStats } from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useStartSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
} from "@store/gameApi";
import { RootState } from "@store/store";
import {
  getLevelParams,
  getZoneCenterPct,
  generateTrials,
  type TrialSpec,
  type StimulusType,
} from "./reactionTapContent";
import { gameContinueButtonSx } from "@components/Home/Games/gameUi";
import { useGameTimingMode } from "@hooks/useGameTimingMode";
import { adjustReactionTapTiming } from "@utils/gameTiming";

export interface ReactionTapGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

const ROUNDS_PER_SESSION = 3;
const MIN_TARGET_SIZE_PX = 72;
const PREMATURE_FEEDBACK_MS = 800;
const TAP_FEEDBACK_MS = 800;
const SESSION_START_DEDUPE_MS = 2000;

let lastSessionStartAt = 0;

type Phase = "idle" | "waiting" | "stimulus" | "tapFeedback" | "roundFeedback";

interface TrialResult {
  trialIndex: number;
  type: StimulusType;
  stimulusOnset: number;
  tapTime: number | null;
  reactionTime: number | null;
  isPremature: boolean;
  isNoGo: boolean;
  correct: boolean;
}

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

export const ReactionTap: FC<ReactionTapGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId
  );
  const sessionStartedRef = useRef(false);
  const [startSession] = useStartSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(null);
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();

  const contentSeed = effectiveGame?.contentSeed ?? "";
  const difficulty = effectiveGame
    ? Math.max(1, Math.min(10, effectiveGame.difficulty))
    : 1;
  const gameTimingMode = useGameTimingMode();
  const levelParams = useMemo(
    () => adjustReactionTapTiming(getLevelParams(difficulty), gameTimingMode),
    [difficulty, gameTimingMode]
  );

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [roundCount, setRoundCount] = useState(0);
  const [trials, setTrials] = useState<TrialSpec[]>([]);
  const [trialIndex, setTrialIndex] = useState(0);
  const [trialResults, setTrialResults] = useState<TrialResult[]>([]);
  const [stimulusOnsetTime, setStimulusOnsetTime] = useState<number | null>(null);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<
    { accuracy: number; avgReactionMs: number; prematureTaps: number; inhibitionErrors: number; missedTargets: number }[]
  >([]);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<Record<string, number>>({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(null);
  const [muted, setMuted] = useState(false);
  const [tapFeedback, setTapFeedback] = useState<"none" | "correct" | "tooSoon" | "nogoError">("none");
  const [prematureTapsThisRound, setPrematureTapsThisRound] = useState(0);

  const stimulusOnsetRef = useRef<number | null>(null);
  const waitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const missTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nogoAdvanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentTrial = trials[trialIndex] ?? null;

  const startNewRound = useCallback(
    (roundIndex?: number) => {
      const round = roundIndex ?? roundCount;
      const seed = contentSeed ? `${contentSeed}-${round}` : `local-${round}`;
      const newTrials = generateTrials(difficulty, seed, round);
      setTrials(newTrials);
      setTrialIndex(0);
      setTrialResults([]);
      setRoundStartTime(Date.now());
      setStimulusOnsetTime(null);
      setStimulusOnsetRef(null);
      setXpEarned(null);
      setTapFeedback("none");
      setPrematureTapsThisRound(0);
      setPhase("waiting");
    },
    [contentSeed, roundCount, difficulty]
  );

  function setStimulusOnsetRef(v: number | null) {
    stimulusOnsetRef.current = v;
  }

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAt < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAt = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    startSession({ patientId: selectedPatientId })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId) {
          setSessionId(res.sessionId);
          setSessionStartTime(Date.now());
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [isGuided, isFreePlayFromLibrary, selectedPatientId, startSession, t]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(Date.now());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary]);

  useEffect(() => {
    if (sessionStartTime == null || paused) return;
    const id = setInterval(() => {
      setTimerSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, paused]);

  // Waiting phase: schedule stimulus after random delay
  useEffect(() => {
    if (phase !== "waiting" || !currentTrial || paused) return;
    const delay =
      levelParams.minDelayMs +
      Math.floor(
        Math.random() * (levelParams.maxDelayMs - levelParams.minDelayMs + 1)
      );
    waitTimeoutRef.current = setTimeout(() => {
      const onset = performance.now();
      setStimulusOnsetTime(onset);
      stimulusOnsetRef.current = onset;
      setPhase("stimulus");
      waitTimeoutRef.current = null;
    }, delay);
    return () => {
      if (waitTimeoutRef.current) {
        clearTimeout(waitTimeoutRef.current);
        waitTimeoutRef.current = null;
      }
    };
  }, [phase, currentTrial, trialIndex, levelParams, paused, trials.length]);

  // Stimulus phase: schedule miss (go) or auto-advance (nogo-only). Must be separate from waiting
  // effect so its cleanup does not clear timers that were just set when phase flips waiting→stimulus.
  useEffect(() => {
    if (phase !== "stimulus" || !currentTrial || paused) return;

    const onset = stimulusOnsetRef.current ?? stimulusOnsetTime ?? performance.now();
    const isNogoOnlyTrial =
      (currentTrial.type === "nogo" && !currentTrial.positions) ||
      (currentTrial.positions != null &&
        !currentTrial.positions.some((p) => p.type === "go"));

    if (currentTrial.type === "go" && !currentTrial.positions) {
      missTimeoutRef.current = setTimeout(() => {
        setTrialResults((prev) => [
          ...prev,
          {
            trialIndex,
            type: "go",
            stimulusOnset: onset,
            tapTime: null,
            reactionTime: null,
            isPremature: false,
            isNoGo: false,
            correct: false,
          },
        ]);
        setTapFeedback("none");
        setTrialIndex((i) => i + 1);
        missTimeoutRef.current = null;
        if (trialIndex + 1 >= trials.length) {
          setPhase("roundFeedback");
        } else {
          setPhase("waiting");
          setStimulusOnsetTime(null);
          stimulusOnsetRef.current = null;
        }
      }, levelParams.maxReactionMs);
    } else if (currentTrial.positions?.some((p) => p.type === "go")) {
      missTimeoutRef.current = setTimeout(() => {
        setTrialResults((prev) => [
          ...prev,
          {
            trialIndex,
            type: "go",
            stimulusOnset: onset,
            tapTime: null,
            reactionTime: null,
            isPremature: false,
            isNoGo: false,
            correct: false,
          },
        ]);
        setTapFeedback("none");
        setTrialIndex((i) => i + 1);
        missTimeoutRef.current = null;
        if (trialIndex + 1 >= trials.length) {
          setPhase("roundFeedback");
        } else {
          setPhase("waiting");
          setStimulusOnsetTime(null);
          stimulusOnsetRef.current = null;
        }
      }, levelParams.maxReactionMs);
    } else if (isNogoOnlyTrial) {
      // Inhibition trial (red forms): advance after maxReactionMs if user correctly did not tap
      nogoAdvanceTimeoutRef.current = setTimeout(() => {
        setTrialResults((prev) => [
          ...prev,
          {
            trialIndex,
            type: currentTrial.type,
            stimulusOnset: onset,
            tapTime: null,
            reactionTime: null,
            isPremature: false,
            isNoGo: true,
            correct: true,
          },
        ]);
        setTapFeedback("none");
        setTrialIndex((i) => i + 1);
        nogoAdvanceTimeoutRef.current = null;
        if (trialIndex + 1 >= trials.length) {
          setPhase("roundFeedback");
        } else {
          setPhase("waiting");
          setStimulusOnsetTime(null);
          stimulusOnsetRef.current = null;
        }
      }, levelParams.maxReactionMs);
    }

    return () => {
      if (missTimeoutRef.current) {
        clearTimeout(missTimeoutRef.current);
        missTimeoutRef.current = null;
      }
      if (nogoAdvanceTimeoutRef.current) {
        clearTimeout(nogoAdvanceTimeoutRef.current);
        nogoAdvanceTimeoutRef.current = null;
      }
    };
  }, [
    phase,
    currentTrial,
    trialIndex,
    levelParams,
    paused,
    trials.length,
    stimulusOnsetTime,
  ]);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleAreaTap = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (paused) return;
      const now = performance.now();
      const el = containerRef.current ?? (e.currentTarget as HTMLElement);
      const rect = el.getBoundingClientRect();
      const clientX = "changedTouches" in e ? (e as React.TouchEvent).changedTouches[0]?.clientX : (e as React.MouseEvent).clientX;
      const clientY = "changedTouches" in e ? (e as React.TouchEvent).changedTouches[0]?.clientY : (e as React.MouseEvent).clientY;

      if (phase === "waiting") {
        setPrematureTapsThisRound((c) => c + 1);
        setTapFeedback("tooSoon");
        setTimeout(() => setTapFeedback("none"), PREMATURE_FEEDBACK_MS);
        return;
      }

      if (phase !== "stimulus" || !currentTrial) return;

      // User tapped; clear nogo auto-advance timeout so we don't double-advance
      if (nogoAdvanceTimeoutRef.current) {
        clearTimeout(nogoAdvanceTimeoutRef.current);
        nogoAdvanceTimeoutRef.current = null;
      }

      const onset = stimulusOnsetRef.current ?? stimulusOnsetTime ?? 0;
      const reactionTime = now - onset;

      if (currentTrial.positions) {
        const hitRadiusPx = 40;
        let hitType: StimulusType | null = null;
        for (const p of currentTrial.positions) {
          const cx = rect.left + (p.xPct / 100) * rect.width;
          const cy = rect.top + (p.yPct / 100) * rect.height;
          const dist = Math.sqrt((clientX - cx) ** 2 + (clientY - cy) ** 2);
          if (dist <= hitRadiusPx) {
            hitType = p.type;
            break;
          }
        }
        if (hitType === "go") {
          if (missTimeoutRef.current) {
            clearTimeout(missTimeoutRef.current);
            missTimeoutRef.current = null;
          }
          setTrialResults((prev) => [
            ...prev,
            {
              trialIndex,
              type: "go",
              stimulusOnset: onset,
              tapTime: now,
              reactionTime,
              isPremature: false,
              isNoGo: false,
              correct: true,
            },
          ]);
          setTapFeedback("correct");
        } else if (hitType === "nogo" || hitType === "distractor") {
          setTrialResults((prev) => [
            ...prev,
            {
              trialIndex,
              type: currentTrial.type,
              stimulusOnset: onset,
              tapTime: now,
              reactionTime: null,
              isPremature: false,
              isNoGo: true,
              correct: false,
            },
          ]);
          setTapFeedback("nogoError");
        }
      } else {
        if (currentTrial.type === "go") {
          if (missTimeoutRef.current) {
            clearTimeout(missTimeoutRef.current);
            missTimeoutRef.current = null;
          }
          setTrialResults((prev) => [
            ...prev,
            {
              trialIndex,
              type: "go",
              stimulusOnset: onset,
              tapTime: now,
              reactionTime,
              isPremature: false,
              isNoGo: false,
              correct: true,
            },
          ]);
          setTapFeedback("correct");
        } else {
          setTrialResults((prev) => [
            ...prev,
            {
              trialIndex,
              type: currentTrial.type,
              stimulusOnset: onset,
              tapTime: now,
              reactionTime: null,
              isPremature: false,
              isNoGo: true,
              correct: false,
            },
          ]);
          setTapFeedback("nogoError");
        }
      }

      setTimeout(() => {
        setTapFeedback("none");
        setTrialIndex((i) => {
          const next = i + 1;
          if (next >= trials.length) {
            setPhase("roundFeedback");
          } else {
            setPhase("waiting");
            setStimulusOnsetTime(null);
            stimulusOnsetRef.current = null;
          }
          return next;
        });
      }, TAP_FEEDBACK_MS);
    },
    [
      phase,
      currentTrial,
      trialIndex,
      trials.length,
      stimulusOnsetTime,
      paused,
    ]
  );

  useEffect(() => {
    return () => {
      if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current);
      if (missTimeoutRef.current) clearTimeout(missTimeoutRef.current);
      if (nogoAdvanceTimeoutRef.current) clearTimeout(nogoAdvanceTimeoutRef.current);
    };
  }, []);

  const handleNextRound = useCallback(() => {
    const truePositives = trialResults.filter((r) => r.type === "go" && r.correct).length;
    const falsePositives = trialResults.filter((r) => r.isNoGo && r.tapTime != null).length;
    const missedMatches = trialResults.filter((r) => r.type === "go" && !r.correct).length;
    const total = trialResults.length;
    const accuracy =
      total > 0
        ? truePositives / Math.max(1, truePositives + falsePositives + missedMatches)
        : 0;
    const reactionTimes = trialResults
      .filter((r) => r.type === "go" && r.reactionTime != null)
      .map((r) => r.reactionTime!);
    const avgReactionMs =
      reactionTimes.length > 0
        ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
        : 0;
    const inhibitionErrors = trialResults.filter((r) => r.isNoGo && r.tapTime != null).length;
    const missedTargets = trialResults.filter((r) => r.type === "go" && !r.correct).length;

    const nextRound = roundCount + 1;
    setRoundResults((prev) => [
      ...prev,
      {
        accuracy,
        avgReactionMs,
        prematureTaps: prematureTapsThisRound,
        inhibitionErrors,
        missedTargets,
      },
    ]);

    if (sessionId) {
      submitRound({
        sessionId,
        body: {
          gameId: GAME_IDS.reactionTap,
          roundNumber: nextRound,
          difficulty,
          accuracy,
          reactionTimeMs: reactionTimes.length > 0 ? Math.round(avgReactionMs) : null,
          completionTimeMs: Date.now() - roundStartTime,
          errorCount: inhibitionErrors + missedTargets,
          rawData: {
            trials: trialResults.map((r) => ({
              stimulusType: r.type,
              stimulusOnset: r.stimulusOnset,
              tapTime: r.tapTime,
              reactionTime: r.reactionTime,
              isPremature: r.isPremature,
              isNoGo: r.isNoGo,
            })),
            avgReactionTime: reactionTimes.length > 0 ? avgReactionMs : null,
            prematureTaps: prematureTapsThisRound,
            inhibitionErrors,
            missedTargets,
          },
        },
      })
        .unwrap()
        .then((res) => {
          if (res?.xpEarned != null) {
            setXpEarned(res.xpEarned);
            setSessionScore((s) => s + res.xpEarned);
          }
          if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
            setDomainUpdatesSum((prev) => {
              const next = { ...prev };
              for (const [k, v] of Object.entries(res.domainUpdates)) {
                next[k] = (next[k] ?? 0) + v;
              }
              return next;
            });
          }
        })
        .catch(() => {});
    } else {
      const xp = accuracy >= 0.8 ? 20 : 10;
      setXpEarned(xp);
      setSessionScore((s) => s + xp);
    }

    setRoundCount(nextRound);

    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      const allResults = [
        ...roundResults,
        { accuracy, avgReactionMs, prematureTaps: prematureTapsThisRound, inhibitionErrors, missedTargets },
      ];
      const totalRounds = allResults.length;
      const avgAccuracy =
        totalRounds > 0
          ? (allResults.reduce((s, r) => s + r.accuracy, 0) / totalRounds) * 100
          : 0;
      const avgReactionSec =
        totalRounds > 0
          ? allResults.reduce((s, r) => s + r.avgReactionMs, 0) / totalRounds / 1000
          : 0;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgReactionSec,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgReactionSec,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: avgAccuracy,
          avgReactionSeconds: avgReactionSec,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      lastSessionStartAt = 0;
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    startNewRound(nextRound);
  }, [
    roundCount,
    trialResults,
    roundStartTime,
    sessionId,
    isGuided,
    onGuidedGameComplete,
    completeSession,
    submitRound,
    startNewRound,
    roundResults,
    sessionScore,
    domainUpdatesSum,
    difficulty,
    prematureTapsThisRound,
  ]);

  const handleExit = useCallback(() => {
    if (sessionId) completeSession({ sessionId }).catch(() => {});
    lastSessionStartAt = 0;
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleSummaryContinue = useCallback(() => navigate(-1), [navigate]);

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "roundFeedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  const feedbackText =
    phase === "roundFeedback"
      ? null
      : tapFeedback === "correct"
        ? t("games.reactionTap.feedbackCorrect")
        : tapFeedback === "tooSoon"
          ? t("games.reactionTap.feedbackTooSoon")
          : tapFeedback === "nogoError"
            ? t("games.reactionTap.feedbackNoGo")
            : null;

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary stats={summaryStats} onContinue={handleSummaryContinue} />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.reactionTap.title")}
      level={difficulty}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={() => setPaused((p) => !p)}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
    >
      {phase === "idle" && (
        <Box
          textAlign="center"
          py={4}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.reactionTap.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.reactionTap.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => startNewRound()}
            sx={gameContinueButtonSx}
          >
            {t("games.reactionTap.startRound")}
          </Button>
        </Box>
      )}

      {(phase === "waiting" || phase === "stimulus") && (
        <Box
          ref={containerRef}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
          position="relative"
          onClick={handleAreaTap}
          onTouchEnd={(e) => {
            e.preventDefault();
            handleAreaTap(e);
          }}
          sx={{
            cursor: "pointer",
            touchAction: "manipulation",
            bgcolor: phase === "stimulus" && tapFeedback === "tooSoon" ? "warning.light" : "background.default",
            transition: "background-color 0.2s",
          }}
          role="button"
          tabIndex={0}
          aria-label={phase === "waiting" ? t("games.reactionTap.waitingA11y") : t("games.reactionTap.tapTargetA11y")}
        >
          {phase === "stimulus" && currentTrial && (
            <>
              {currentTrial.positions ? (
                currentTrial.positions.map((p, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: "absolute",
                      left: `${p.xPct}%`,
                      top: `${p.yPct}%`,
                      transform: "translate(-50%, -50%)",
                      width: MIN_TARGET_SIZE_PX,
                      height: MIN_TARGET_SIZE_PX,
                      borderRadius: "50%",
                      bgcolor:
                        p.type === "go"
                          ? "success.main"
                          : p.type === "nogo"
                            ? "error.main"
                            : "grey.500",
                      boxShadow: 2,
                      pointerEvents: "none",
                    }}
                  />
                ))
              ) : (
                <Box
                  sx={{
                    position: "absolute",
                    left: `${getZoneCenterPct(currentTrial.zoneIndex).xPct}%`,
                    top: `${getZoneCenterPct(currentTrial.zoneIndex).yPct}%`,
                    transform: "translate(-50%, -50%)",
                    width: MIN_TARGET_SIZE_PX,
                    height: MIN_TARGET_SIZE_PX,
                    borderRadius: "50%",
                    bgcolor: currentTrial.type === "go" ? "success.main" : "error.main",
                    boxShadow: 2,
                    pointerEvents: "none",
                  }}
                />
              )}
            </>
          )}
          {phase === "waiting" && (
            <Typography variant="body2" color="text.secondary">
              {t("games.reactionTap.getReady")}
            </Typography>
          )}
        </Box>
      )}

      {phase === "roundFeedback" && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          mt={2}
          mb={2}
        >
          <Typography variant="body1" color="text.secondary">
            {t("games.reactionTap.avgReactionDisplay", {
              ms: Math.round(
                trialResults
                  .filter((r) => r.reactionTime != null)
                  .reduce((a, r) => a + (r.reactionTime ?? 0), 0) /
                  Math.max(
                    1,
                    trialResults.filter((r) => r.reactionTime != null).length
                  )
              ),
            })}
          </Typography>
          {xpEarned != null && (
            <Typography variant="body2" color="primary.main">
              +{xpEarned} XP
            </Typography>
          )}
          <Button
            variant="contained"
            size="large"
            onClick={handleNextRound}
            sx={gameContinueButtonSx}
          >
            {roundCount + 1 >= ROUNDS_PER_SESSION
              ? t("games.reactionTap.finishSession")
              : t("games.reactionTap.nextRound")}
          </Button>
        </Box>
      )}
    </GameScreenShell>
  );
};

export default ReactionTap;
